import * as React from 'react';
import { useEffect, useState } from 'react';
import { API, Storage } from 'aws-amplify';
import Dropzone from 'react-dropzone';
import {
  X,
  Loader2,
  Upload,
  Youtube,
  Facebook,
  CloudUpload,
} from 'lucide-react';
import { createLessonDetail } from '../../../../graphql/mutations';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { FormField } from '../../../components/ui/form-field';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';

declare const global: {
  me: { orgID: string };
  trainingVideos: Record<string, any[]> | null;
};

type UploadType = 'video' | 'pdf' | 'youtube' | 'vimeo' | 'facebook';

interface UploadLessonModalProps {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
  snackbar: (message: string, severity: string) => void;
  category?: string;
}

interface LessonForm {
  name: string;
  description: string;
  url: string;
  order: number;
  imageName: string;
  imageType: string;
  posterImageName: string;
  category: string;
}

export function UploadLessonModal({
  open,
  onClose,
  refresh,
  snackbar,
  category,
}: UploadLessonModalProps) {
  const initialForm: LessonForm = {
    name: '',
    description: '',
    url: '',
    order: 0,
    imageName: '',
    imageType: 'video',
    posterImageName: '',
    category: category || '',
  };

  const [file, setFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | Blob | null>(null);
  const [lessonDetails, setLessonDetails] = useState<LessonForm>(initialForm);
  const [uploadType, setUploadType] = useState<UploadType>('video');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [useExternalThumb, setUseExternalThumb] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [disableForm, setDisableForm] = useState(false);
  const [openThumbnailPicker, setOpenThumbnailPicker] = useState(false);
  const [createNewCategory, setCreateNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [sliderVal, setSliderVal] = useState(0);

  const validate = (form: LessonForm): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.name || !form.name.length) {
      errs.name = 'Title is required';
    }
    if (!form.category || !form.category.length) {
      errs.category = 'Category is required';
    }
    if (
      uploadType === 'youtube' ||
      uploadType === 'vimeo' ||
      uploadType === 'facebook'
    ) {
      if (!form.url || !form.url.length) {
        errs.url = 'Please provide a video link';
      } else if (
        uploadType === 'youtube' &&
        !form.url.includes('watch?v=') &&
        !form.url.includes('youtu.be/') &&
        !form.url.includes('youtube.com/embed/') &&
        !form.url.includes('youtube.com/shorts/')
      ) {
        errs.url = 'Invalid YouTube link';
      } else if (uploadType === 'vimeo' && !form.url.includes('vimeo.com/')) {
        errs.url = 'Invalid Vimeo link';
      } else if (
        uploadType === 'facebook' &&
        !form.url.includes('facebook.com') &&
        !form.url.includes('fb.watch')
      ) {
        errs.url = 'Invalid Facebook link';
      }
    }
    if (
      (uploadType === 'video' || uploadType === 'pdf') &&
      !form.imageName.length
    ) {
      errs.imageName =
        uploadType === 'pdf'
          ? 'Please select a PDF file'
          : 'Please select a video file';
    }
    return errs;
  };

  const resetForm = () => {
    setFile(null);
    setImageFile(null);
    setUploadType('video');
    setScreenshots([]);
    setLessonDetails(initialForm);
    setErrors({});
    setUploadProgress(0);
    setSubmitted(false);
    setUploading(false);
    setUploadStatus(null);
    setDisableForm(false);
    setNewCategory('');
    setCreateNewCategory(false);
    setUseExternalThumb(false);
  };

  const handleFileDrop = async (droppedFiles: File[]) => {
    const formats = [
      'mov',
      'avi',
      'mp4',
      'mpeg',
      'ogv',
      'ts',
      '3gp',
      '3g2',
      'pdf',
    ];
    const droppedFile = droppedFiles[0];
    const ext = droppedFile.name.split('.').pop()?.toLowerCase() || '';
    if (!formats.includes(ext)) {
      snackbar(
        uploadType === 'pdf'
          ? 'File must be a PDF'
          : 'Video format must be mov, avi, mp4, mpeg, ogv, ts, 3gp, or 3g2',
        'error'
      );
      return;
    }
    setScreenshots([]);
    setFile(droppedFile);
    setLessonDetails((prev) => ({ ...prev, imageName: droppedFile.name }));

    // Generate video screenshots for thumbnail picker
    if (uploadType !== 'pdf') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(droppedFile);

      video.onloadedmetadata = async () => {
        const numScreenshots = 100;
        const duration = video.duration;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const captured: string[] = [];

        for (let i = 0; i < numScreenshots; i++) {
          const time = (i / (numScreenshots - 1)) * duration;
          video.currentTime = time;
          await new Promise((resolve) =>
            video.addEventListener('seeked', resolve, { once: true })
          );
          canvas.width = video.videoWidth * 0.5;
          canvas.height = video.videoHeight * 0.5;
          context?.drawImage(video, 0, 0, canvas.width, canvas.height);
          captured.push(canvas.toDataURL('image/jpeg'));
        }
        setScreenshots(captured);
      };
    }
  };

  const handleThumbnailDrop = (droppedFiles: File[]) => {
    const formats = ['jpeg', 'jpg', 'png', 'gif'];
    const image = droppedFiles[0];
    const ext = image.name.split('.').pop()?.toLowerCase() || '';
    if (!formats.includes(ext)) {
      snackbar('Thumbnail must be a PNG, GIF, or JPEG image', 'error');
      return;
    }
    setImageFile(image);
    setLessonDetails((prev) => ({
      ...prev,
      posterImageName: image.name.replace(/^.*[\\/]/, ''),
    }));
  };

  const saveScreenshot = () => {
    const dataURL = screenshots[sliderVal];
    const formatMatch = dataURL.match(/^data:image\/(jpeg|png);base64,/);
    if (!formatMatch) {
      return;
    }
    const base64Data = dataURL.split(',')[1];
    const binaryData = atob(base64Data);
    const imageName = `screenshot_${sliderVal}_${Date.now()}_${file?.name
      ?.split('.')[0]
      ?.replace(/^.*[\\/]/, '')}.jpeg`;

    const blob = new Blob(
      [
        new Uint8Array(binaryData.length).map((_, i) =>
          binaryData.charCodeAt(i)
        ),
      ],
      { type: 'image/jpeg' }
    );

    setImageFile(blob);
    setLessonDetails((prev) => ({ ...prev, posterImageName: imageName }));
    setSliderVal(0);
    setOpenThumbnailPicker(false);
  };

  const handleUpload = async () => {
    setSubmitted(true);
    const validationErrors = validate(lessonDetails);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setDisableForm(true);
    setUploading(true);
    setUploadStatus('Preparing files for upload');

    const submitObj: any = {
      lessonID: 'NA',
      isDeleted: 'false',
      orgID: global.me.orgID,
      name: lessonDetails.name,
      category: lessonDetails.category,
      imageType: lessonDetails.imageType,
      imageName: '',
      url: '',
      urlType: '',
      posterImageName: '',
      description: lessonDetails.description,
      order: lessonDetails.order,
    };

    if (
      uploadType === 'youtube' ||
      uploadType === 'vimeo' ||
      uploadType === 'facebook'
    ) {
      setUploadStatus('Processing video link...');

      if (uploadType === 'youtube') {
        const url = lessonDetails.url;
        let videoId = '';
        if (url.includes('watch?v=')) {
          videoId = url.split('watch?v=')[1].split('&')[0];
        } else if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1].split('?')[0];
        } else if (url.includes('youtube.com/embed/')) {
          videoId = url.split('youtube.com/embed/')[1].split('?')[0];
        } else if (url.includes('youtube.com/shorts/')) {
          videoId = url.split('youtube.com/shorts/')[1].split('?')[0];
        }
        submitObj.url = videoId;
        if (useExternalThumb) {
          submitObj.posterImageName = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        }
      } else if (uploadType === 'vimeo') {
        submitObj.url = lessonDetails.url;
        if (useExternalThumb) {
          try {
            const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
              lessonDetails.url
            )}`;
            const response = await fetch(oembedUrl);
            const data = await response.json();
            if (data?.thumbnail_url) {
              submitObj.posterImageName = data.thumbnail_url.replace(
                '_295x166',
                '_640'
              );
            } else {
              snackbar(
                'Could not get Vimeo thumbnail. Please upload a custom one.',
                'error'
              );
              setDisableForm(false);
              setUploading(false);
              return;
            }
          } catch (error) {
            snackbar('Could not get Vimeo thumbnail.', 'error');
            setDisableForm(false);
            setUploading(false);
            return;
          }
        }
      } else if (uploadType === 'facebook') {
        submitObj.url = lessonDetails.url;
        if (!imageFile && !lessonDetails.posterImageName) {
          try {
            const oembedUrl = `https://www.facebook.com/plugins/video/oembed.json?url=${encodeURIComponent(
              lessonDetails.url
            )}`;
            const response = await fetch(oembedUrl);
            if (response.ok) {
              const data = await response.json();
              if (data?.thumbnail_url) {
                submitObj.posterImageName = data.thumbnail_url;
              }
            }
          } catch (_) {
            // Facebook oembed may not always be available
          }
        }
      }
      submitObj.urlType = uploadType;
    } else if (uploadType === 'video' || uploadType === 'pdf') {
      setUploadStatus('Uploading file...');
      submitObj.imageName = `${submitObj.orgID}_${file?.name?.replace(
        /^.*[\\/]/,
        ''
      )}`;
      try {
        await Storage.put(submitObj.imageName, file!, {
          progressCallback(progress: { loaded: number; total: number }) {
            setUploadProgress((progress.loaded / progress.total) * 100);
          },
          contentType: file!.type,
        });
      } catch (error) {
        console.error('Upload error:', error);
        snackbar('File upload failed', 'error');
        setDisableForm(false);
        setUploading(false);
        return;
      }
    }

    if (
      !useExternalThumb &&
      imageFile &&
      lessonDetails.posterImageName.length
    ) {
      setUploadStatus('Saving thumbnail...');
      if (!lessonDetails.posterImageName.startsWith('https://')) {
        submitObj.posterImageName = `${
          submitObj.orgID
        }_${lessonDetails.posterImageName.replace(/^.*[\\/]/, '')}`;
        await Storage.put(submitObj.posterImageName, imageFile, {
          contentType:
            imageFile instanceof File ? imageFile.type : 'image/jpeg',
        });
      } else {
        submitObj.posterImageName = lessonDetails.posterImageName;
      }
    }

    setUploadStatus('Saving training info...');
    try {
      await API.graphql({
        query: createLessonDetail,
        variables: { input: submitObj },
      });
      snackbar(`Training Media Uploaded - ${submitObj.name}`, 'success');
      resetForm();
      refresh();
      onClose();
    } catch (err) {
      console.error('Error creating lesson:', err);
      snackbar('Failed to upload training media', 'error');
      setDisableForm(false);
      setUploading(false);
    }
  };

  useEffect(() => {
    setErrors(validate(lessonDetails));
  }, [lessonDetails, uploadType]);

  useEffect(() => {
    setLessonDetails((prev) => ({ ...prev, imageType: uploadType }));
    setLessonDetails((prev) => ({ ...prev, posterImageName: '' }));
    if (uploadType === 'video') {
      setUseExternalThumb(false);
    }
  }, [uploadType]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (disableForm) {
            snackbar('Please wait until upload is complete', 'error');
          } else {
            resetForm();
            onClose();
          }
        }
      }}
    >
      {/* Thumbnail picker overlay */}
      {openThumbnailPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-background rounded-xl p-6 max-w-lg w-full mx-4 space-y-4">
            <p className="font-semibold text-lg text-foreground text-center">
              Pick Thumbnail From Video
            </p>
            {screenshots.length > 0 ? (
              <>
                <img
                  src={screenshots[sliderVal]}
                  alt="Screenshot"
                  className="w-full aspect-video rounded-lg object-cover"
                />
                <input
                  type="range"
                  min={0}
                  max={99}
                  step={1}
                  value={sliderVal}
                  onChange={(e) => setSliderVal(parseInt(e.target.value))}
                  className="w-full accent-primary"
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">
                  Loading screenshots...
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button disabled={!screenshots.length} onClick={saveScreenshot}>
                Save Image
              </Button>
              <Button
                variant="outline"
                onClick={() => setOpenThumbnailPicker(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main upload modal */}
      <div className="bg-background rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col relative">
        {/* Close button */}
        <button
          onClick={() => {
            if (disableForm) {
              snackbar('Please wait until upload is complete', 'error');
            } else {
              resetForm();
              onClose();
            }
          }}
          className="absolute top-3 right-3 z-10 p-1.5 bg-destructive rounded-md hover:bg-destructive/90 transition-colors"
        >
          <X className="size-4 text-white" />
        </button>

        {/* Upload type tabs */}
        <div className="flex justify-center border-b border-border p-3 gap-1 flex-wrap">
          {(
            [
              { key: 'video', label: 'Upload Video' },
              { key: 'pdf', label: 'Upload PDF' },
              { key: 'youtube', label: 'YouTube' },
              { key: 'vimeo', label: 'Vimeo' },
              { key: 'facebook', label: 'Facebook' },
            ] as { key: UploadType; label: string }[]
          ).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setUploadType(tab.key)}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
                uploadType === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto flex flex-col md:flex-row">
          {/* Left: Form */}
          <div className="flex-1 p-5 space-y-4 border-r border-border">
            <p className="font-semibold text-foreground">General Info</p>

            <FormField
              label="Title"
              error={submitted ? errors.name : undefined}
            >
              <Input
                disabled={disableForm}
                value={lessonDetails.name}
                onChange={(e) =>
                  setLessonDetails((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Training media title..."
              />
            </FormField>

            <FormField label="Description">
              <Textarea
                disabled={disableForm}
                rows={3}
                value={lessonDetails.description}
                onChange={(e) =>
                  setLessonDetails((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Training media description..."
              />
            </FormField>

            <div className="flex gap-3">
              <div className="flex-1">
                <FormField
                  label="Category"
                  error={submitted ? errors.category : undefined}
                >
                  {createNewCategory ? (
                    <div className="flex gap-2">
                      <Input
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="New category..."
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        disabled={!newCategory.length}
                        onClick={() => {
                          setLessonDetails((prev) => ({
                            ...prev,
                            category: newCategory,
                          }));
                          setCreateNewCategory(false);
                        }}
                      >
                        Create
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCreateNewCategory(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Select
                      disabled={disableForm}
                      value={lessonDetails.category}
                      onValueChange={(val) => {
                        if (val === '@add-new-category') {
                          setCreateNewCategory(true);
                        } else {
                          setLessonDetails((prev) => ({
                            ...prev,
                            category: val,
                          }));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {global.trainingVideos &&
                          Object.keys(global.trainingVideos).map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        {newCategory.length > 0 && (
                          <SelectItem value={newCategory}>
                            {newCategory}
                          </SelectItem>
                        )}
                        <SelectItem value="@add-new-category">
                          + Add New Category
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
              </div>
              <div className="w-20">
                <FormField label="Order">
                  <Input
                    type="number"
                    disabled={disableForm}
                    value={lessonDetails.order}
                    onChange={(e) =>
                      setLessonDetails((prev) => ({
                        ...prev,
                        order: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </FormField>
              </div>
            </div>

            {/* Thumbnail section */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground pl-1">
                Thumbnail Image
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  disabled
                  value={lessonDetails.posterImageName}
                  placeholder="No thumbnail selected"
                  className="flex-1"
                />
                <Dropzone
                  onDrop={handleThumbnailDrop}
                  disabled={useExternalThumb || disableForm}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={useExternalThumb || disableForm}
                      >
                        Choose Image
                      </Button>
                    </div>
                  )}
                </Dropzone>
                {(uploadType === 'youtube' || uploadType === 'vimeo') && (
                  <div className="flex items-center gap-2 border border-border rounded-md px-3 py-1.5">
                    <span className="text-xs text-muted-foreground">
                      Use {uploadType === 'youtube' ? 'YouTube' : 'Vimeo'}{' '}
                      Thumbnail
                    </span>
                    <Switch
                      checked={useExternalThumb}
                      onCheckedChange={setUseExternalThumb}
                      disabled={disableForm}
                    />
                  </div>
                )}
                {uploadType === 'video' && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={disableForm || !file}
                    onClick={() => setOpenThumbnailPicker(true)}
                  >
                    From Video
                  </Button>
                )}
              </div>
            </div>

            {/* Upload button */}
            <Button
              className="w-full"
              disabled={disableForm}
              onClick={handleUpload}
            >
              UPLOAD
            </Button>
          </div>

          {/* Right: Dropzone / URL input */}
          <div className="flex-1 p-5 flex flex-col items-center justify-center min-h-[300px]">
            {uploadType === 'video' || uploadType === 'pdf' ? (
              !uploading ? (
                <Dropzone onDrop={handleFileDrop}>
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      <input {...getInputProps()} />
                      <CloudUpload className="size-16 text-muted-foreground mb-4" />
                      <p className="font-semibold text-foreground">
                        Drag and drop to upload
                      </p>
                      <Button variant="outline" className="mt-4">
                        {file ? 'Change File' : 'Choose File'}
                      </Button>
                      {file && (
                        <p className="text-sm text-muted-foreground mt-2 truncate max-w-full">
                          {lessonDetails.imageName}
                        </p>
                      )}
                      {submitted && errors.imageName && (
                        <p className="text-sm text-destructive mt-2">
                          {errors.imageName}
                        </p>
                      )}
                    </div>
                  )}
                </Dropzone>
              ) : (
                <div className="w-full flex flex-col items-center justify-center gap-4">
                  {uploadStatus && (
                    <p className="font-semibold text-foreground">
                      {uploadStatus}
                    </p>
                  )}
                  {uploadType === 'video' &&
                  screenshots.length > 0 &&
                  uploadProgress < 100 ? (
                    <img
                      src={screenshots[Math.floor(uploadProgress)]}
                      alt="Upload progress"
                      className="w-full max-w-[400px] rounded-lg"
                    />
                  ) : (
                    <Loader2 className="size-8 animate-spin text-primary" />
                  )}
                  {/* Progress bar */}
                  <div className="w-full bg-muted rounded-full h-6 relative overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-300 to-green-600 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                      {Math.floor(uploadProgress)}%
                    </span>
                  </div>
                </div>
              )
            ) : (
              <div className="w-full flex flex-col items-center justify-center gap-4">
                {uploading && uploadStatus ? (
                  <>
                    <p className="font-semibold text-foreground">
                      {uploadStatus}
                    </p>
                    <Loader2 className="size-8 animate-spin text-primary" />
                  </>
                ) : (
                  <>
                    {uploadType === 'youtube' && (
                      <Youtube className="size-20 text-muted-foreground" />
                    )}
                    {uploadType === 'facebook' && (
                      <Facebook className="size-20 text-muted-foreground" />
                    )}
                    {uploadType === 'vimeo' && (
                      <div className="size-20 flex items-center justify-center">
                        <img
                          src="/images/vimeo.svg"
                          alt="Vimeo"
                          className="h-16"
                        />
                      </div>
                    )}
                    <p className="font-semibold text-foreground">
                      {uploadType === 'youtube'
                        ? 'Paste YouTube Link Below'
                        : uploadType === 'facebook'
                        ? 'Paste Facebook Link Below'
                        : 'Paste Vimeo Link Below'}
                    </p>
                    <Input
                      disabled={disableForm}
                      value={lessonDetails.url}
                      onChange={(e) =>
                        setLessonDetails((prev) => ({
                          ...prev,
                          url: e.target.value,
                        }))
                      }
                      placeholder={
                        uploadType === 'youtube'
                          ? 'https://youtube.com/watch?v=...'
                          : uploadType === 'facebook'
                          ? 'https://facebook.com/...'
                          : 'https://vimeo.com/...'
                      }
                      className="w-full max-w-sm"
                    />
                    {submitted && errors.url && (
                      <p className="text-sm text-destructive">{errors.url}</p>
                    )}
                    {uploadType === 'facebook' && (
                      <p className="text-xs text-muted-foreground text-center max-w-sm">
                        Facebook Reels may not support embedding on external
                        platforms. If this video fails to play, try uploading it
                        as a standard Facebook video instead of a Reel.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
